import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import companiesQuery from "../../firestore/Companies.query";
import { MatchingSetting } from "../../model/MatchingSetting";
import Button from "../../components/button/Button";
import { ModalAndAlmightyDialog } from "../../components/modal/ModalAndAlmightyDialog";
import { requestMatchingAdd } from "../../api/Companies.api";
import { Spiner } from "../../components/spinner/Spiner";
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from "@heroicons/react/24/outline";

export const MatchingRequest = () => {

  // 利用者番号のハッシュ値と団体ID
  const { hash, tenantId, step = '1' } = useParams<{hash: string, tenantId: string, step: string | undefined}>();
  const navigate = useNavigate();

  const [matchingSetting, setMatchingSetting] = useState<MatchingSetting>();
  const [selectedContent, setSelectedContent] = useState<{value: string, index: number}>();
  const [selectedArea, setSelectedArea] = useState<{value: string, index: number}>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoad, setIsLoad] = useState<boolean>(false);

  const onSelectContent = (content: string, index: number) => {
    setSelectedContent({value: content, index});
  }
  const onAreaSelect = () => {
    navigate(`/match-req/${tenantId}/${hash}/2`);
  }
  const onSelectArea = (area: string, index: number) => {
    setSelectedArea({value: area, index});
  }
  const onRequest = () => {
    navigate(`/match-req/${tenantId}/${hash}/3`);
  }
  const onSubmit = async () => {
    try{
      if(!selectedArea && !selectedContent){
        throw new Error('依頼内容とお住いの地域を選択してください。');
      }
      if(!selectedContent){
        throw new Error('依頼内容を選択してください。');
      }
      if(!selectedArea){
         throw new Error('お住いの地域を選択してください。');
      }
      if(!tenantId){
        throw new Error('団体情報取得に失敗しました。担当者へご連絡ください。');
      }
      if(!hash){
        throw new Error('利用者情報取得に失敗しました。担当者へご連絡ください。');
      }
      setIsLoad(true);
      // 依頼内容をAPIに送信
      await requestMatchingAdd(tenantId, hash, selectedContent.value, selectedArea.value)
        .then((resolve) => {
          if(resolve.success){
            setIsSuccess(true);
          }
        })
        .catch((reject) => {
          throw new Error(`サーバーエラーが発生しました。担当者へご連絡ください。${reject.data.message}`);
        });
      setIsLoad(false);
      setSelectedContent(undefined);
      setSelectedArea(undefined);
      navigate(`/match-req/${tenantId}/${hash}/4`);
    } catch (err: any){
      setErrorMessage(err.message)
      setIsOpen(true);
    }
  }

  useEffect(() => {
    const f = async () => {
      try{
        setIsLoad(true);
        if(tenantId === undefined){
          throw new Error();
        }
        // 依頼項目取得
        const matchingSettingResult = await companiesQuery.getMatchingSetting(tenantId);
        if(!matchingSettingResult.success){
          throw new Error();
        }
        setMatchingSetting(matchingSettingResult.data);
        setIsLoad(false);
      } catch (err: any) {
        console.error(err);
      }
    }
    f();
  },[tenantId]);

  if(step === '3' && (selectedArea === undefined || selectedContent === undefined)){
    navigate(`/match-req/${tenantId}/${hash}/1`);
  }
  if(step === '4' && !isSuccess){
    navigate(`/match-req/${tenantId}/${hash}/1`);
  }

  return step !== '4'
    ? (<>
      <article className="flex flex-col space-y-6 p-4 h-full">
        <ul className={`flex space-x-2 justify-center`}>
          <li className={`flex space-x-1 text-xl items-center ${step === '1' ? `text-indigo-500 font-black` : 'text-indigo-400'}`}>
            <div className={`flex rounded-full ${step === '1' ? 'bg-indigo-500 text-white border-6 w-10 h-10' : 'border w-6 h-6'} border-indigo-500 items-center justify-center`}>1</div>
            <div>内容選択</div>
          </li>
          <li className={`flex space-x-2 text-xl items-center ${step === '2' ? `text-indigo-500 font-black` : 'text-indigo-400'}`}>
            <div className={`flex rounded-full ${step === '2' ? 'bg-indigo-500 text-white border-6 w-10 h-10' : 'border w-6 h-6'} border-indigo-500 items-center justify-center`}>2</div>
            <div>地域選択</div>
          </li>
          <li className={`flex space-x-2 text-xl items-center ${step === '3' ? `text-indigo-500 font-black` : 'text-indigo-400'}`}>
            <div className={`flex rounded-full ${step === '3' ? 'bg-indigo-500 text-white border-6 w-10 h-10' : 'border w-6 h-6'} border-indigo-500 items-center justify-center`}>3</div>
            <div>確認</div>
          </li>
        </ul>
        {step === '1' && (<section id={'contents'} className="flex flex-col space-y-2">
          <h2 className="text-2xl font-black">依頼する内容はどれですか？</h2>
          <ul className="grid grid-cols-2 gap-2">
            {
              matchingSetting?.contents.map((content, index) => {
                return (
                  <li key={`${content}-${index}`}>
                    <Button
                      outline={selectedContent?.value !== content}
                      color={selectedContent?.value === content ? "green" :"dark"}
                      disabled={!matchingSetting.contentsEnable.at(index)}
                      className="flex flex-col h-full items-center w-full font-black text-xl px-0"
                      onClick={() => onSelectContent(content, index)}
                    >
                      <div className="whitespace-pre">{matchingSetting.contentsDisp?.at(index)?.replace('\\n', '\n') ?? content}</div>
                      {!matchingSetting.contentsEnable.at(index) && (<div className="text-red-500 text-base">準備中</div>)}
                    </Button>
                  </li>
                )
              })
            }
          </ul>
          <Button
            className="w-full text-2xl items-center"
            disabled={selectedContent === undefined}
            onClick={onAreaSelect}
            children={
              (<>地域を選ぶ{<ChevronDoubleRightIcon className="h-8 w-8 p-0 m-0"/>}</>)
            } />
        </section>)}
        {step === '2' && (<section id={'areas'} className="flex flex-col space-y-2">
          <h2 className="text-2xl font-black">お住いの地域はどこですか？</h2>
          <ul className="grid grid-cols-2 gap-2">
            {
              matchingSetting?.areas.map((area, index) => {
                return (
                  <li key={`${area}-${index}`}>
                    <Button
                      outline={selectedArea?.value !== area}
                      color={selectedArea?.value === area ? "green" : "dark"}
                      disabled={!matchingSetting.areasEnable.at(index)}
                      className="flex flex-col h-full items-center w-full font-black text-xl px-0"
                      onClick={() => onSelectArea(area, index)}
                    >
                      <div className="whitespace-pre">{matchingSetting.areasDisp?.at(index)?.replace('\\n', '\n') ?? area}</div>
                      {!matchingSetting.areasEnable.at(index) && (<div className="text-red-500 text-base">準備中</div>)}
                    </Button>
                  </li>
                )
              })
            }
          </ul>
          <Button
            className="w-full text-2xl items-center"
            disabled={selectedArea === undefined}
            onClick={onRequest}
            children={
              (<>依頼内容確認{<ChevronDoubleRightIcon className="h-8 w-8 p-0 m-0"/>}</>)
            } />
        </section>)}
        {step === '3' && (<section className="flex flex-col space-y-4">
          <h2 className="text-2xl font-black">以下の内容で依頼しますか？</h2>
          <ul className="flex flex-col text-xl space-y-4">
            <li className="flex space-x-2 items-center">
              <span className="whitespace-nowrap w-[4.5rem]">内容 :</span>
              <span className="border-2 border-black rounded-xl p-2 font-black text-2xl whitespace-pre">{matchingSetting?.contentsDisp?.at(selectedContent?.index ?? 0)?.replace('\\n', '\n')}</span>
            </li>
            <li className="flex space-x-2 items-center">
              <span className="whitespace-nowrap w-[4.5rem]">地域 :</span>
              <span className="border-2 border-black rounded-xl p-2 font-black text-2xl whitespace-pre">{matchingSetting?.areasDisp?.at(selectedArea?.index ?? 0)?.replace('\\n', '\n')}</span>
            </li>
          </ul>
          <Button
            outline
            color="red"
            disabled={!(selectedContent !== undefined && selectedArea !== undefined)}
            className="whitespace-pre w-full font-black text-xl items-center"
            children={(<><ChevronDoubleLeftIcon className="h-6 w-6 -mr-3"/><ChevronDoubleLeftIcon className="h-6 w-6"/>やり直す</>)}
            onClick={() => {
              navigate(`/match-req/${tenantId}/${hash}/1`);
            }}
          />
          <Button
            children={'依頼する'}
            disabled={!(selectedContent !== undefined && selectedArea !== undefined)}
            className="whitespace-pre w-full font-black text-xl"
            onClick={onSubmit}
          />
        </section>)}
        {isOpen && (<ModalAndAlmightyDialog
          toggle={() => {}}
          open={isOpen}
          title={"ご確認ください"}
          children={errorMessage}
          actions={[{children:'閉じる', onClick: () => setIsOpen(false)}]}
          closeOnClickOutside={false}
        />)}
      </article>
      {isLoad && (
      <div className="absolute top-0 left-0 flex min-h-full items-center bg-gray-300 opacity-80 w-full justify-center">
        <Spiner />
      </div>
      )}</>
    )
    :(
      <article className="flex min-h-full items-center space-y-6 p-4">
        <section className="space-y-4 text-2xl">
          <h1 className="font-black">ボランティアグループに依頼しました。</h1>
          <p>ボランティアから連絡が来ますので、お待ちください。</p>
          <p>尚、ボランティアの状況次第では<br />ご希望に添えない場合があります。</p>
          <p>ご了承ください。</p>
        </section>
      </article>
    );
}